<template>
  <div>
    <v-dialog
      v-model="dialog"
      :max-width="dialogSize"
      scrollable
      persistent
    >
      <!-- Non-form modal -->
      <v-card v-if="!isForm" class="custom-modal-card">
        <v-card-title class="d-flex justify-space-between align-center custom-modal-title">
          <span>{{ title }}</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="closeModal"
          />
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-4 custom-modal-body">
          <slot />
        </v-card-text>

        <v-divider v-if="$slots.footer || hasDefaultFooter" />

        <v-card-actions v-if="$slots.footer || hasDefaultFooter" class="pa-4 custom-modal-actions">
          <slot name="footer">
            <!-- Default close button for non-form modal -->
            <v-spacer />
            <v-btn
              color="secondary"
              variant="text"
              @click="closeModal"
            >
              Close
            </v-btn>
          </slot>
        </v-card-actions>
      </v-card>

      <!-- Form modal -->
      <v-card v-else class="custom-modal-card">
        <v-card-title class="d-flex justify-space-between align-center custom-modal-title">
          <span>{{ title }}</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="closeModal"
          />
        </v-card-title>

        <v-divider />

        <v-form @submit.prevent="submitForm">
          <v-card-text class="pa-4 custom-modal-body">
            <slot />
          </v-card-text>

          <v-divider v-if="$slots.footer || hasDefaultFooter" />

          <v-card-actions v-if="$slots.footer || hasDefaultFooter" class="pa-4 custom-modal-actions">
            <slot name="footer">
              <!-- Default footer buttons for form -->
              <v-spacer />
              <v-btn
                color="secondary"
                variant="text"
                @click="closeModal"
              >
                Close
              </v-btn>
              <v-btn
                color="primary"
                type="submit"
                variant="elevated"
              >
                Submit
              </v-btn>
            </slot>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { v4 as generateUUID } from "uuid";

export default {
  name: "CustomModal",
  data() {
    return {
      dialog: false,
      id: `modal-${generateUUID()}`, // Unique ID for this modal instance
    };
  },
  props: {
    isForm: {
      type: Boolean,
      required: false,
      default: false, // Whether it's a form modal
    },
    title: {
      type: String,
      required: true, // Modal title
    },
    size: {
      type: String,
      required: false,
      default: "xl", // Modal size (default is 'xl')
    },
    hasDefaultFooter: {
      type: Boolean,
      required: false,
      default: true, // Whether to show default footer with buttons
    },
  },
  computed: {
    dialogSize() {
      // Convert Bootstrap modal sizes to Vuetify dialog sizes
      const sizeMap = {
        sm: "500px",
        md: "700px",
        lg: "900px",
        xl: "1200px",
      };
      return sizeMap[this.size] || sizeMap.xl;
    },
  },
  methods: {
    // Open the modal
    openModal() {
      this.dialog = true;
      this.$nextTick(() => {
        // Focus on the first focusable element
        const modal = this.$el.querySelector('.v-dialog');
        if (modal) {
          const firstFocusable = modal.querySelector("input, button, select, textarea");
          firstFocusable && firstFocusable.focus();
        }
      });
      this.$emit("open"); // Emit an 'open' event
    },

    // Close the modal
    closeModal() {
      this.dialog = false;
      this.$emit("close"); // Emit a 'close' event
    },

    // Submit the form and close the modal
    submitForm() {
      this.$emit("submit"); // Emit a 'submit' event first
      this.closeModal(); // Then close the modal
    },
  },
  watch: {
    // Watch for external changes to dialog state
    dialog(newVal) {
      if (newVal) {
        this.$emit("open");
      } else {
        this.$emit("close");
      }
    },
  },
};
</script>

<style scoped>
.custom-modal-card {
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  border: 1px solid rgb(var(--v-theme-border-color));
  box-shadow: 0 14px 36px rgba(var(--v-theme-on-surface-rgb), 0.18);
}

.custom-modal-title {
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
}

.custom-modal-actions {
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgb(var(--v-theme-border-color));
}

/* Improve input borders inside modals (dark mode was rendering near-black) */
:deep(.v-theme--dark .custom-modal-card .v-field--variant-outlined .v-field__outline__start),
:deep(.v-theme--dark .custom-modal-card .v-field--variant-outlined .v-field__outline__end),
:deep(.v-theme--dark .custom-modal-card .v-field--variant-outlined .v-field__outline__notch) {
  border-color: rgba(var(--v-theme-on-surface-rgb), 0.35) !important;
}

:deep(.v-theme--dark .custom-modal-card .v-field--variant-outlined.v-field--focused .v-field__outline__start),
:deep(.v-theme--dark .custom-modal-card .v-field--variant-outlined.v-field--focused .v-field__outline__end),
:deep(.v-theme--dark .custom-modal-card .v-field--variant-outlined.v-field--focused .v-field__outline__notch) {
  border-color: rgb(var(--v-theme-primary)) !important;
}

:deep(.v-theme--dark .custom-modal-card .v-field__input) {
  background-color: rgba(var(--v-theme-surface), 0.9);
}
</style>
